/**
 * Face detector.
 *
 * @copyright Realeyes OU. All rights reserved.
 */


import img from './utils/img';
import haar from './utils/haar';


export default class FaceDetector {
    /**
     * Creates FaceDetector instance.
     *
     * @param {*} classifier Haar cascade
     * @param {number} width Source width
     * @param {number} height Source height
     * @param {number} maxWorkSize Biggest dimension to which the source image will be resized
     * @param {number} startScale Initial scale
     * @param {number} scaleFactor Scale factor for each step
     */
    constructor(classifier, width, height, maxWorkSize, startScale, scaleFactor) {
        this.classifier = classifier;
        this.startScale = startScale;
        this.scaleFactor = scaleFactor;
        this.maxWorkSize = maxWorkSize;
        this.width = width;
        this.height = height;
        this.scale = Math.min(this.maxWorkSize / this.width, this.maxWorkSize / this.height);
        this.scaledWidth = (this.width * this.scale) | 0;
        this.scaledHeight = (this.height * this.scale) | 0;
        this.workspace = new ArrayBuffer(this.scaledWidth * this.scaledHeight);
        this.intSum = new Uint32Array((this.scaledWidth + 1) * (this.scaledHeight + 1));
        this.intSqSum = new Uint32Array((this.scaledWidth + 1) * (this.scaledWidth + 1));
    }

    /**
     * Resolves with a rectangle for the detected face or with null if the face is not found.
     *
     * @param {ArrayBuffer} image Source image
     * @return {{
     *   x: number,
     *   y: number,
     *   width: number,
     *   height: number,
     * }|null}
     */
    detect(image) {
        const sourceImage = img.resize(image, this.width, this.height, this.scaledWidth, this.scaledHeight);

        img.grayscale(sourceImage, this.workspace);
        img.equalizeHistogram(this.workspace, this.workspace);
        img.computeIntegralImage(
            this.workspace,
            this.scaledWidth,
            this.scaledHeight,
            this.intSum.buffer,
            this.intSqSum.buffer,
        );

        const rects = haar.groupRectangles(
            haar.detect(
                this.intSum,
                this.intSqSum,
                this.scaledWidth,
                this.scaledHeight,
                this.classifier,
                this.scaleFactor,
                this.startScale,
            ),
        );

        if (rects.length > 0) {
            const res = rects.sort((a, b) => a.confidence > b.confidence)[0];

            return {
                x: (res.x / this.scale) | 0,
                y: (res.y / this.scale) | 0,
                width: (res.width / this.scale) | 0,
                height: (res.height / this.scale) | 0,
            };
        }

        return null;
    }
}
